import React, { useMemo, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import programRowStyles from './ProgramRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import ProgramRow from './ProgramRow';
import ListContent from '../composites/ListContent';
import AddProgramDialog from './AddProgramDialog';
import EditProgramTemplate from './EditProgramTemplate';
import { GymType } from '../../constants';
import { getDefaultProgram } from '../../utils';
import { fetchProgramById, fetchProgramsPageByTrainerIds } from '../../api';
import { queryKeys } from '../../api/queryKeys';
import { usePaginatedListQuery } from '../../hooks/usePaginatedListQuery';
import { removeItemFromPaginatedQueries } from '../../api/pagination';
import { useSessionData, useUserAction } from '../../providers';
import { useErrorContext } from '../../providers/errorProvider';
// Removed: EditTemplate is now handled internally by EditProgramTemplate

import type { Program, WithOptionalId } from '../../types';

const programListTitles = [
  'Program Name',
  'Created By',
  '1X1 CLIENTS',
  'WEEKS',
  'LAST EDIT', // Added new column
  'Options',
];

const Programs: React.FC = () => {
  const { createProgram, updateProgramAndRefreshPrograms, deleteProgram } =
    useUserAction();
  const { loginTrainer, gym, programs, trainers } = useSessionData();
  const { setGlobalError } = useErrorContext();
  const queryClient = useQueryClient();
  const isGymOfTypeSchool = gym?.type === GymType.School;

  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const [isAddProgramModalOpen, setIsAddProgramModalOpen] = useState(false);
  // removed template modal management
  const [editedProgram, setEditedProgram] =
    useState<WithOptionalId<Program> | null>(null);
  // Template editing is handled inside EditProgramTemplate via DrillStack
  // No need to hide a parent wrapper with ContentDrill
  const programTrainerIds = useMemo(() => {
    const trainerIds = Object.keys(trainers);
    return trainerIds.length
      ? trainerIds
      : loginTrainer
        ? [loginTrainer.id]
        : [];
  }, [loginTrainer, trainers]);

  const programsListQuery = usePaginatedListQuery<Program>({
    enabled: Boolean(loginTrainer),
    queryKey: (params) =>
      queryKeys.programs.trainerIdsPage(programTrainerIds, params),
    queryFn: (params) =>
      fetchProgramsPageByTrainerIds(programTrainerIds, params),
  });

  const filteredPrograms = useMemo(() => {
    return programsListQuery.items
      .map((program) => ({
        ...programs[program.id],
        ...program,
      }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [programs, programsListQuery.items]);

  const programsPagination = {
    page: programsListQuery.page,
    pageSize: programsListQuery.pageSize,
    totalElements: programsListQuery.meta.totalElements,
    totalPages: programsListQuery.meta.totalPages,
    onPageChange: programsListQuery.setPage,
    onPageSizeChange: programsListQuery.setPageSize,
    disabled: programsListQuery.isFetching,
  };

  const invalidateProgramsPage = () => {
    void queryClient.invalidateQueries({ queryKey: ['programs'] });
  };

  // Moved above early return to satisfy rules-of-hooks
  const handleSubmitEditProgramTemplate = useCallback(
    async (newProgramData: WithOptionalId<Program>): Promise<boolean> => {
      if (!loginTrainer) return false;
      const success = newProgramData.id
        ? await updateProgramAndRefreshPrograms(newProgramData as Program)
        : (await createProgram(newProgramData, loginTrainer.id)).success;
      if (!success) {
        return false;
      }
      setIsAddProgramModalOpen(false);
      invalidateProgramsPage();
      return true;
    },
    [updateProgramAndRefreshPrograms, createProgram, loginTrainer],
  );

  if (!loginTrainer) {
    return (
      <EmptyState
        icon={<SummarizeOutlinedIcon />}
        text="No Programs for logged in role."
      />
    );
  }

  const renderEmptyState = () => (
    <EmptyState
      icon={<SummarizeOutlinedIcon />}
      cta={{
        icon: <AddBoxOutlinedIcon color="primary" />,
        label: 'Add A Program',
        onClick: () =>
          isGymOfTypeSchool
            ? CreateDefaultProgramAndMoveToEdit()
            : setIsAddProgramModalOpen(true),
      }}
      text="No Programs Have Been Added"
    />
  );

  const CreateDefaultProgramAndMoveToEdit = () => {
    const newDefaultProgram = getDefaultProgram(loginTrainer.id);
    setEditedProgram(newDefaultProgram);
  };

  const renderAddProgramDialog = () => (
    <AddProgramDialog
      title="Add Program"
      trainerId={loginTrainer.id}
      addProgram={setEditedProgram}
      onClose={() => setIsAddProgramModalOpen(false)}
      isOpen={isAddProgramModalOpen}
    />
  );

  const removeProgram = async (program: Program) => {
    const success = await deleteProgram(program);
    if (!success) {
      return;
    }

    removeItemFromPaginatedQueries<Program>(
      queryClient,
      ['programs'],
      program.id,
    );
    invalidateProgramsPage();
  };

  const loadProgramDetails = async (
    program: Program,
  ): Promise<Program | null> => {
    const { data, success, error } = await fetchProgramById(program.id);
    if (!success || !data) {
      setGlobalError(error?.message || 'Error loading program details');
      return null;
    }
    return data;
  };

  const openEditProgram = async (program: Program) => {
    const detailedProgram = await loadProgramDetails(program);
    if (!detailedProgram) return;
    setEditedProgram(detailedProgram);
  };

  const duplicateProgram = async (program: Program) => {
    const detailedProgram = await loadProgramDetails(program);
    if (!detailedProgram) return;
    const newProgramData = {
      ...detailedProgram,
      name: `${detailedProgram.name} - Copy`,
      id: undefined,
      trainerId: loginTrainer.id,
    };
    await createProgram(newProgramData, loginTrainer.id);
    invalidateProgramsPage();
  };

  // Removed: routine/template editing now lives inside EditProgram

  const renderContent = () => {
    if (editedProgram) return null;

    return (
      <ListContent
        items={filteredPrograms}
        isLoading={programsListQuery.isLoading}
        listHeaderClassName={programRowStyles.listGrid}
        pagination={programsPagination}
        EmptyStateComponent={renderEmptyState}
        titles={programListTitles}
        ItemComponent={({ data }) => (
          <ProgramRow
            program={data}
            deleteProgram={removeProgram}
            allowDelete={loginTrainer.id === data.trainerId}
            duplicateProgram={duplicateProgram}
            openEditProgramModal={() => openEditProgram(data)}
            selected={selectedProgram === data.id}
            onSelect={() => setSelectedProgram(data.id)}
            key={data.id}
          />
        )}
      />
    );
  };

  // Removed: EditTemplate drill-in

  const handleCloseEditProgramTemplate = () => {
    setEditedProgram(null);
  };

  const getDrillInContentEditProgramTemplate = () => {
    if (!editedProgram) return null;
    return (
      <EditProgramTemplate
        program={editedProgram}
        onSubmit={handleSubmitEditProgramTemplate}
        onClose={handleCloseEditProgramTemplate}
        // setHideWrapper removed with ContentDrill
        proceedAfterSave={true}
      />
    );
  };

  const getDrillInContent = () => {
    if (editedProgram) {
      return getDrillInContentEditProgramTemplate();
    }
    return null;
  };

  const handleDashboardBack = () => {
    if (editedProgram) {
      handleCloseEditProgramTemplate();
    }
  };

  const getSearchPlaceholder = () => {
    if (!filteredPrograms.length) return '';
    return 'Search for Programs';
  };

  return (
    <ContentDrill
      title={
        editedProgram
          ? editedProgram?.id
            ? 'Edit Program'
            : 'Add Program'
          : 'Programs'
      }
      onBack={editedProgram ? () => handleDashboardBack() : undefined}
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add A Program',
          onClick: () =>
            isGymOfTypeSchool
              ? CreateDefaultProgramAndMoveToEdit()
              : setIsAddProgramModalOpen(true),
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      searchQuery={programsListQuery.searchQuery}
      setSearchQuery={programsListQuery.setSearchQuery}
      disableSearch={false}
      isDrilledIn={Boolean(editedProgram)}
      drillInContent={getDrillInContent()}
    >
      {renderContent()}
      {renderAddProgramDialog()}
    </ContentDrill>
  );
};

export default Programs;
